import { pgSelect, pgInsert, pgUpdateWhere, isSupabaseConfigured } from './supabaseClient';
import { logEvent } from './debugLog';
import { LANGS, type Lang } from '../i18n';

/**
 * Per-venue settings, keyed by MultiSet map code. v1: which UI languages the
 * venue offers its visitors (Dashboard → Languages). Unconfigured venues
 * offer everything — restriction is opt-in.
 */

interface VenueSettingsRow {
  map_code: string;
  enabled_langs: string[];
  features?: Record<string, boolean>;
}

/** Feature scopes: core app surfaces + AI agents + INDUSTRY PACKS
 *  (2026-07-12 overnight build — each industry is its own category so a
 *  venue enables exactly the vertical it is). */
export type FeatureScope =
  | 'visitor'
  | 'dashboard'
  | 'ai'
  | 'corporate'
  | 'retail'
  | 'healthcare'
  | 'museums';

export const SCOPE_LABELS: Record<FeatureScope, string> = {
  visitor: 'Visitor app',
  dashboard: 'Dashboard',
  ai: '🤖 AI agents',
  corporate: '🏢 Corporate campus',
  retail: '🛍 Retail & malls',
  healthcare: '🏥 Healthcare',
  museums: '🖼 Museums & venues',
};

/** Platform feature catalog — Super Admin toggles these per venue. */
export const FEATURES: Array<{ key: string; label: string; scope: FeatureScope; defaultOff?: boolean }> = [
  // MINIMAL AR HUD BY DEFAULT (2026-07-12, user spec): visitors see only
  // 🧭 Navigation and ✕ Stop out of the box — every other HUD button is a
  // feature that must be EXPLICITLY enabled (defaultOff) per map.
  { key: 'tours', label: 'Guided tours', scope: 'visitor' },
  { key: 'media', label: 'POI media in AR', scope: 'visitor' },
  { key: 'explore', label: 'Explore mode (🏷)', scope: 'visitor', defaultOff: true },
  { key: 'parking', label: 'Parking memory (🚗)', scope: 'visitor', defaultOff: true },
  { key: 'proximity', label: 'Proximity alerts', scope: 'visitor' },
  { key: 'capture', label: 'Photo/video capture + PDF report', scope: 'visitor' },
  { key: 'ocrTranslate', label: 'Sign translation (🔤 point camera at signs)', scope: 'visitor', defaultOff: true },
  { key: 'relocalize', label: 'Relocalize button (🔄)', scope: 'visitor', defaultOff: true },
  { key: 'voiceToggle', label: 'Voice guidance toggle (🗣)', scope: 'visitor', defaultOff: true },
  // 🔊 AI Voice (2026-07-18, user request "Can Ask NavMe be in Eleven Labs
  // style"): user explicitly did NOT want a paid TTS vendor wired in
  // ("I dont need direct ElevenLabs") — this upgrades ALL spoken guidance
  // (turn-by-turn cues, arrivals, tours, Ask NavMe) to prefer the best
  // natural-sounding voice already installed on the visitor's device
  // (iOS Enhanced/Premium Siri voices, Android Google neural voices)
  // instead of the default robotic pick — zero cost, zero new API/secret.
  { key: 'aiVoice', label: 'AI Voice — natural voice for all guidance (🔊)', scope: 'visitor', defaultOff: true },
  { key: 'map2d', label: '2D map button (🗺)', scope: 'visitor', defaultOff: true },
  { key: 'debugTools', label: 'Debug report button (🐞)', scope: 'visitor', defaultOff: true },
  { key: 'arAddPoi', label: 'Add POI in AR (📍 Mark POI here)', scope: 'visitor', defaultOff: true },
  { key: 'arPinPoint', label: 'Pin-point POI placement in AR (🎯)', scope: 'visitor', defaultOff: true },
  { key: 'campaigns', label: 'Campaign scheduling', scope: 'dashboard' },
  { key: 'partnerApi', label: 'Partner API keys', scope: 'dashboard' },
  { key: 'analytics', label: 'Analytics card', scope: 'dashboard' },
  { key: 'qr', label: 'QR deep links', scope: 'dashboard' },
  // 🤖 AI agents (per-map toggles; the ANTHROPIC_API_KEY function secret
  // must ALSO be set for the LLM paths to run).
  { key: 'concierge', label: 'Concierge agent (💬 chat, multi-stop routes)', scope: 'ai', defaultOff: true },
  { key: 'locCoach', label: 'Localization coach', scope: 'ai' },
  { key: 'opsDigest', label: 'Daily ops digest email', scope: 'ai' },
  { key: 'visitStory', label: 'Visit story in PDF reports', scope: 'ai' },
  { key: 'contentAgent', label: 'Content writer + POI translations', scope: 'ai' },
  // 🏢 Corporate campus
  { key: 'facilityReq', label: 'Facility issue reporting (🛠)', scope: 'corporate', defaultOff: true },
  { key: 'checkin', label: 'Visitor check-in (🪪, host notification)', scope: 'corporate', defaultOff: true },
  // 🛍 Retail & malls
  { key: 'offers', label: 'Offers & promotions on POIs (🏷)', scope: 'retail', defaultOff: true },
  { key: 'productFinder', label: 'Product finder search', scope: 'retail', defaultOff: true },
  // 🏥 Healthcare
  { key: 'appointments', label: 'Appointment wayfinding links (?appt=)', scope: 'healthcare', defaultOff: true },
  { key: 'accessibleDefault', label: 'Accessibility-first routing default (♿)', scope: 'healthcare', defaultOff: true },
  { key: 'audienceModes', label: 'Patient/visitor audience modes (?aud=)', scope: 'healthcare', defaultOff: true },
  // 🖼 Museums & venues
  { key: 'exhibitMode', label: 'Exhibit mode (manual next-stop, narration)', scope: 'museums', defaultOff: true },
];

const OFFLINE_KEY = 'navme.cache.venueLangs';
const FEATURES_OFFLINE_KEY = 'navme.cache.venueFeatures';
let cache: Lang[] | null = null;
let featureCache: Record<string, boolean> | null = null;

function sanitize(codes: string[]): Lang[] {
  const valid = codes.filter((c): c is Lang => LANGS.some((l) => l.code === c));
  return valid.length > 0 ? valid : ['en'];
}

/** Languages this venue offers. All languages when unconfigured/offline-less. */
export async function getEnabledLangs(mapCode: string, forceRefresh = false): Promise<Lang[]> {
  if (cache && !forceRefresh) return cache;
  const all = LANGS.map((l) => l.code);
  if (!isSupabaseConfigured() || !mapCode) return all;
  try {
    const rows = await pgSelect<VenueSettingsRow>(
      'venue_settings',
      `select=*&map_code=eq.${encodeURIComponent(mapCode)}`,
    );
    cache = rows.length > 0 ? sanitize(rows[0].enabled_langs) : all;
    try {
      localStorage.setItem(OFFLINE_KEY, JSON.stringify(cache));
    } catch {
      /* quota */
    }
    return cache;
  } catch {
    try {
      const snap = JSON.parse(localStorage.getItem(OFFLINE_KEY) ?? 'null') as Lang[] | null;
      if (snap) return sanitize(snap);
    } catch {
      /* corrupt */
    }
    return all;
  }
}

/**
 * Loads the venue's feature flags (missing keys = enabled). Call at boot;
 * afterwards featureEnabled() answers synchronously everywhere.
 */
export async function loadFeatures(mapCode: string, forceRefresh = false): Promise<Record<string, boolean>> {
  if (featureCache && !forceRefresh) return featureCache;
  if (!isSupabaseConfigured() || !mapCode) {
    featureCache = {};
    return featureCache;
  }
  try {
    const rows = await pgSelect<VenueSettingsRow & { customer_id?: string | null }>(
      'venue_settings',
      `select=features,customer_id&map_code=eq.${encodeURIComponent(mapCode)}`,
    );
    const toggles = rows[0]?.features ?? {};
    // Multi-tenant: a feature is live only if the owning customer's
    // ENTITLEMENT (customers.features, super-managed) allows it AND the
    // per-map toggle (venue_settings.features, customer-managed) is on.
    let entitlement: Record<string, boolean> = {};
    if (rows[0]?.customer_id) {
      const cust = await pgSelect<{ features: Record<string, boolean> }>(
        'customers',
        `select=features&id=eq.${encodeURIComponent(rows[0].customer_id)}`,
      );
      entitlement = cust[0]?.features ?? {};
    }
    const effective: Record<string, boolean> = {};
    for (const f of FEATURES) {
      effective[f.key] = entitlement[f.key] !== false && toggles[f.key] !== false;
    }
    featureCache = effective;
    try {
      localStorage.setItem(FEATURES_OFFLINE_KEY, JSON.stringify(featureCache));
    } catch {
      /* quota */
    }
  } catch {
    try {
      featureCache = JSON.parse(localStorage.getItem(FEATURES_OFFLINE_KEY) ?? '{}') as Record<string, boolean>;
    } catch {
      featureCache = {};
    }
  }
  logEvent('session', `features loaded: ${Object.entries(featureCache).filter(([, v]) => v === false).map(([k]) => `-${k}`).join(' ') || 'all enabled'}`);
  return featureCache;
}

/**
 * Synchronous flag check. Explicit venue setting wins; otherwise the
 * catalog default applies (defaultOff features stay hidden until a venue
 * owner enables them — the "minimal AR HUD" policy).
 */
export function featureEnabled(key: string): boolean {
  const v = featureCache?.[key];
  if (v !== undefined) return v !== false;
  const entry = FEATURES.find((f) => f.key === key);
  return !entry?.defaultOff;
}

/** Raw per-map toggles (what the venue owner chose), NOT effective flags. */
export async function getVenueToggles(mapCode: string): Promise<Record<string, boolean>> {
  const rows = await pgSelect<VenueSettingsRow>(
    'venue_settings',
    `select=features&map_code=eq.${encodeURIComponent(mapCode)}`,
  );
  return rows[0]?.features ?? {};
}

/** The owning customer's entitlement for a venue ({} = everything allowed). */
export async function getEntitlement(mapCode: string): Promise<Record<string, boolean>> {
  const rows = await pgSelect<{ customer_id: string | null }>(
    'venue_settings',
    `select=customer_id&map_code=eq.${encodeURIComponent(mapCode)}`,
  );
  const cid = rows[0]?.customer_id;
  if (!cid) return {};
  const cust = await pgSelect<{ features: Record<string, boolean> }>(
    'customers',
    `select=features&id=eq.${encodeURIComponent(cid)}`,
  );
  return cust[0]?.features ?? {};
}

/** Venue owner or super (RLS-enforced): set a venue's per-map toggles. */
export async function setFeatures(mapCode: string, features: Record<string, boolean>): Promise<void> {
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { features },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, features });
  }
  featureCache = null; // force effective (entitlement ∧ toggles) recompute
  logEvent('admin', `features saved for ${mapCode}`);
}

export type ConciergeProvider = 'claude' | 'gemini' | 'kimi';

const CONCIERGE_PROVIDERS: ConciergeProvider[] = ['claude', 'gemini', 'kimi'];

/** Which AI answers Ask NavMe chat for this venue. Unset/unknown → 'claude'. */
export async function getConciergeProvider(mapCode: string): Promise<ConciergeProvider> {
  const rows = await pgSelect<{ concierge_provider: string }>(
    'venue_settings',
    `select=concierge_provider&map_code=eq.${encodeURIComponent(mapCode)}`,
  );
  const v = rows[0]?.concierge_provider;
  return (CONCIERGE_PROVIDERS as string[]).includes(v ?? '') ? (v as ConciergeProvider) : 'claude';
}

/** Staff-only (RLS-enforced): sets which AI answers this venue's Ask NavMe chat. */
export async function setConciergeProvider(mapCode: string, provider: ConciergeProvider): Promise<void> {
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { concierge_provider: provider },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, concierge_provider: provider });
  }
  logEvent('admin', `concierge provider set: ${provider}`);
}

/** Staff-only (RLS-enforced): sets the venue's language offering. */
export async function setEnabledLangs(mapCode: string, langs: Lang[]): Promise<void> {
  const clean = sanitize(langs);
  // Upsert: update by map_code (the PK); 0 rows → insert.
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { enabled_langs: clean },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, enabled_langs: clean });
  }
  cache = clean;
  logEvent('admin', `venue languages set: ${clean.join(', ')}`);
}

/** The venue's published Mattercraft AR experience URL, if set. */
export async function getMattercraftUrl(mapCode: string): Promise<string | null> {
  const rows = await pgSelect<{ mattercraft_url: string | null }>(
    'venue_settings',
    `select=mattercraft_url&map_code=eq.${encodeURIComponent(mapCode)}`,
  );
  return rows[0]?.mattercraft_url ?? null;
}

/** Staff-only (RLS-enforced): sets the venue's Mattercraft experience URL. */
export async function setMattercraftUrl(mapCode: string, url: string): Promise<void> {
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { mattercraft_url: url },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, mattercraft_url: url });
  }
  logEvent('admin', `mattercraft url saved for ${mapCode}`);
}
